import { Check, X } from 'lucide-react';
import type { ComponentProps, CSSProperties, ReactNode } from 'react';
import { cx, initials } from '../utils.ts';
import { Tooltip } from './Tooltip.tsx';
import './display.css';

/** Categorical palette names (mirrors `ColorName` in @tomo/core). */
export const PALETTE = ['gray', 'brown', 'orange', 'yellow', 'green', 'teal', 'blue', 'purple', 'pink', 'red'] as const;
export type PaletteColor = (typeof PALETTE)[number];

/* ---------------------------------------------------------------------------------------------
 * Badge — counts and statuses
 * ------------------------------------------------------------------------------------------- */

export type BadgeTone = 'neutral' | 'accent' | 'success' | 'danger' | 'warning' | 'info';

export interface BadgeProps extends ComponentProps<'span'> {
  tone?: BadgeTone;
  variant?: 'soft' | 'solid' | 'outline';
  size?: 'sm' | 'md';
  icon?: ReactNode;
}

export function Badge({ tone = 'neutral', variant = 'soft', size = 'md', icon, className, children, ...rest }: BadgeProps) {
  return (
    <span className={cx('g-badge', className)} data-tone={tone} data-variant={variant} data-size={size} {...rest}>
      {icon}
      {children}
    </span>
  );
}

/* ---------------------------------------------------------------------------------------------
 * Tag — colored chip for select options, rarities, labels
 * ------------------------------------------------------------------------------------------- */

export interface TagProps extends Omit<ComponentProps<'span'>, 'color'> {
  color?: PaletteColor | string;
  size?: 'sm' | 'md';
  dot?: boolean;
  icon?: ReactNode;
  onRemove?: () => void;
}

export function Tag({ color = 'gray', size = 'md', dot, icon, onRemove, className, children, ...rest }: TagProps) {
  return (
    <span className={cx('g-tag', className)} data-color={color} data-size={size} {...rest}>
      {dot && <span className="g-tag__dot" />}
      {icon}
      <span className="g-tag__label">{children}</span>
      {onRemove && (
        <button
          type="button"
          className="g-tag__remove"
          aria-label="Remove"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <X size={11} strokeWidth={2.5} />
        </button>
      )}
    </span>
  );
}

export function ColorDot({ color = 'gray', size = 8, className }: { color?: PaletteColor | string; size?: number; className?: string }) {
  return <span className={cx('g-dot', className)} data-color={color} style={{ width: size, height: size }} />;
}

/* ---------------------------------------------------------------------------------------------
 * Avatars
 * ------------------------------------------------------------------------------------------- */

function readableOn(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return '#fff';
  const n = parseInt(m[1]!, 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum > 0.35 ? '#15130e' : '#ffffff';
}

export interface AvatarProps {
  name: string;
  color?: string;
  src?: string | null;
  size?: number;
  /** Presence ring (e.g. live collaborators). */
  ring?: boolean;
  className?: string;
  title?: string;
  style?: CSSProperties;
}

export function Avatar({ name, color = '#8a8f9c', src, size = 24, ring, className, title, style }: AvatarProps) {
  return (
    <span
      className={cx('g-avatar', ring && 'g-avatar--ring', className)}
      title={title}
      style={{
        width: size,
        height: size,
        fontSize: Math.max(9, Math.round(size * 0.4)),
        background: color,
        color: readableOn(color),
        ['--avatar-color' as string]: color,
        ...style,
      }}
      aria-label={title ?? name}
      role="img"
    >
      {src ? <img src={src} alt="" /> : initials(name)}
    </span>
  );
}

export interface AvatarStackProps {
  people: { id: string; name: string; color?: string }[];
  max?: number;
  size?: number;
  ring?: boolean;
  className?: string;
}

export function AvatarStack({ people, max = 4, size = 24, ring, className }: AvatarStackProps) {
  const shown = people.slice(0, max);
  const extra = people.length - shown.length;
  return (
    <span className={cx('g-avatar-stack', className)} data-ring={ring || undefined} style={{ ['--stack-size' as string]: `${size}px` }}>
      {shown.map((p) => (
        <Tooltip key={p.id} content={p.name}>
          <span className="g-avatar-stack__item">
            <Avatar name={p.name} color={p.color} size={size} ring={ring} />
          </span>
        </Tooltip>
      ))}
      {extra > 0 && (
        <span className="g-avatar g-avatar--more" style={{ width: size, height: size, fontSize: Math.max(9, Math.round(size * 0.38)) }}>
          +{extra}
        </span>
      )}
    </span>
  );
}

/* ---------------------------------------------------------------------------------------------
 * Card
 * ------------------------------------------------------------------------------------------- */

export interface CardProps extends ComponentProps<'div'> {
  interactive?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  selected?: boolean;
}

export function Card({ interactive, padding = 'md', selected, className, ...rest }: CardProps) {
  return <div className={cx('g-card', className)} data-interactive={interactive || undefined} data-padding={padding} data-selected={selected || undefined} {...rest} />;
}

/* ---------------------------------------------------------------------------------------------
 * Stat bars (numbers shown the way games show them)
 * ------------------------------------------------------------------------------------------- */

export interface StatBarProps {
  value: number | null | undefined;
  max: number;
  min?: number;
  color?: PaletteColor | 'accent';
  size?: 'xs' | 'sm' | 'md';
  /** Render tick marks every 1/ticks of the bar. */
  ticks?: number;
  className?: string;
  label?: ReactNode;
}

export function StatBar({ value, max, min = 0, color = 'accent', size = 'sm', ticks = 10, className, label }: StatBarProps) {
  const pct = value === null || value === undefined || max <= min ? 0 : Math.max(0, Math.min(1, (value - min) / (max - min)));
  return (
    <span
      className={cx('g-statbar', className)}
      data-size={size}
      data-color={color}
      data-ticks={ticks > 0 || undefined}
      role="meter"
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value ?? undefined}
      aria-label={typeof label === 'string' ? label : undefined}
      style={{ ['--pct' as string]: pct, ['--ticks' as string]: ticks }}
    >
      <span className="g-statbar__fill" />
    </span>
  );
}

export function ProgressBar({ value, max = 100, color = 'accent', size = 'sm', className }: Omit<StatBarProps, 'ticks' | 'min' | 'label' | 'max'> & { max?: number }) {
  return <StatBar value={value} max={max} color={color} size={size} ticks={0} className={cx('g-progress', className)} />;
}

export interface StackedSegment {
  key: string;
  value: number;
  color: PaletteColor | string;
  label: ReactNode;
}

export function StackedBar({ segments, height = 8, className }: { segments: StackedSegment[]; height?: number; className?: string }) {
  const total = segments.reduce((s, x) => s + x.value, 0);
  return (
    <div className={cx('g-stacked', className)} style={{ height }}>
      {total > 0 &&
        segments
          .filter((s) => s.value > 0)
          .map((s) => (
            <Tooltip
              key={s.key}
              content={
                <>
                  {s.label}: {s.value}
                </>
              }
            >
              <span className="g-stacked__seg" data-color={s.color} style={{ flexGrow: s.value }} />
            </Tooltip>
          ))}
    </div>
  );
}

/* ---------------------------------------------------------------------------------------------
 * Color swatches picker
 * ------------------------------------------------------------------------------------------- */

export interface ColorSwatchesProps {
  value: string | null | undefined;
  onChange: (color: PaletteColor) => void;
  colors?: readonly PaletteColor[];
  size?: number;
  className?: string;
}

export function ColorSwatches({ value, onChange, colors = PALETTE, size = 22, className }: ColorSwatchesProps) {
  return (
    <div className={cx('g-swatches', className)} role="radiogroup" aria-label="Color">
      {colors.map((c) => (
        <button
          key={c}
          type="button"
          role="radio"
          aria-checked={value === c}
          aria-label={c}
          title={c[0]!.toUpperCase() + c.slice(1)}
          className="g-swatch"
          data-color={c}
          style={{ width: size, height: size }}
          onClick={() => onChange(c)}
        >
          {value === c && <Check size={Math.round(size * 0.6)} strokeWidth={3} />}
        </button>
      ))}
    </div>
  );
}
